<template>
  <div v-if="!result" class="calculation-steps">
    <h2 class="h4">
      Calculating Skewness (Formula)
    </h2>
    <p>
      The Skewness Calculator is a helpful shortcut that speeds up your work. But you can also calculate a population's or a sample’s skewness by hand.
    </p>
    <p class="small-margins">
      The formulas, however, are slightly different from each other. In practice, sample skewness is more common because it allows us to make inferences about the skew of the underlying population.
    </p>
    <p class="small-margins">
      The <span class="bolded-texts">sample skewness formula</span> is the following:
    </p>
    <p class="formulas">
      \[S_K=\left[\frac{n}{(n-1)(n-2)}\right]\sum_{i=1}^{n}\left(\frac{X_i-\bar{X}}{s}\right)^3\]
    </p>
    <p>
      In this formula, <span class="bolded-texts">n</span> denotes the number of observations in the sample, <span class="inline-formulas">\(\bar{X}\)</span> is the sample mean, whereas <span class="bolded-texts">s</span> is the sample standard deviation.
    </p>
    <p class="small-margins">
      Meanwhile, <span class="bolded-texts">population skewness</span> is calculated using the following formula:
    </p>
    <p class="formulas">
      \[\gamma_1=\left[\frac{1}{N}\right]\sum_{i=1}^{n}\left(\frac{X_i-\mu}{\sigma}\right)^3\]
    </p>
    <p>In this formula, <span class="bolded-texts">N</span> denotes the number of observations in the population, <span class="bolded-texts">𝜇</span> is the population mean, whereas <span class="bolded-texts">σ</span> is the population standard deviation.</p>
    <p class="top-margins">
      In both equations, the algebraic sign indicates the skew’s direction. A negative sign indicates a negatively skewed distribution, whereas a positive one suggests a positive skew.
    </p>
    <h2 class="h4 additional-h2">
      How to Calculate Sample and Population Skewness Step by Step
    </h2>
    <p class="bottom-margins">
      Follow these steps to obtain the skewness of your dataset:
    </p>
    <ol>
      <li>
        <p>
          Calculate the <span class="bolded-texts">sample</span> or <span class="bolded-texts">population mean</span> of the dataset:
        </p>
        <p class="formulas">
          \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}\]
        </p>
        <p class="formulas math-jax-bottom-margins">
          \[\mu=\frac{\sum_{i=1}^{n}X_i}{n}\]
        </p>
      </li>
      <li>
        <p>
          Find the <span class="bolded-texts">sample</span> or <span class="bolded-texts">population standard deviation</span> of the dataset:
        </p>
        <p class="formulas">
          \[s=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}}\]
        </p>
        <p class="formulas math-jax-bottom-margins">
          \[\sigma=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\mu)^2}{n}}\]
        </p>
      </li>
      <li>
        <p>
          Standardize each data point by subtracting the <span class="bolded-texts">mean</span> and dividing by the <span class="bolded-texts">sample</span> or <span class="bolded-texts">population standard deviation</span>:
        </p>
        <p class="formulas">
          \[Z=\frac{X_i-\bar{X}}{s}\]
        </p>
        <p class="formulas math-jax-bottom-margins">
          \[Z=\frac{X_i-\mu}{\sigma}\]
        </p>
      </li>
      <li>
        <p>
          Cube each standardized value by raising it to the power of 3:
        </p>
        <p class="formulas">
          \[\left(\frac{X_i-\bar{X}}{s}\right)^3\]
        </p>
        <p class="formulas math-jax-bottom-margins">
          \[\left(\frac{X_i-\mu}{\sigma}\right)^3\]
        </p>
      </li>
      <li>
        <p>
          Sum all the cubed standardized values:
        </p>
        <p class="formulas">
          \[\sum_{i=1}^{n}\left(\frac{X_i-\bar{X}}{s}\right)^3\]
        </p>
        <p class="formulas math-jax-bottom-margins">
          \[\sum_{i=1}^{n}\left(\frac{X_i-\mu}{\sigma}\right)^3\]
        </p>
      </li>
      <li>
        <p>
          Multiply the result by n/(n-1)(n-2) to obtain the <span class="bolded-texts">sample skewness</span> or divide it by the number of data points in the population (N) to obtain the <span class="bolded-texts">population skewness</span>:
        </p>
        <p class="formulas">
          \[S_K=\left[\frac{n}{(n-1)(n-2)}\right]\sum_{i=1}^{n}\left(\frac{X_i-\bar{X}}{s}\right)^3\]
        </p>
        <p class="formulas math-jax-bottom-margins">
          \[\gamma_1=\left[\frac{1}{N}\right]\sum_{i=1}^{n}\left(\frac{X_i-\mu}{\sigma}\right)^3\]
        </p>
      </li>
    </ol>
    <p>
      This calculation returns a value that represents the skewness. Intuitively, positive values indicate positive skewness and negative values indicate a negative one. The skewness’s magnitude indicates the extent of asymmetry in the distribution. Meanwhile, a result of 0 means the distribution is symmetrical.
    </p>
  </div>

  <div v-else :key="result" class="calculation-steps result">
    <h2 class="h4">
      Calculation Steps:
    </h2>
    <p class="bottom-margins">
      Follow these steps to obtain the skewness of your dataset:
    </p>
    <ol>
      <li>
        <p>
          Calculate the <span class="bolded-texts">mean</span> of the dataset:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas math-jax-top-bottom-margins">
          \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{ {{ additionString }} }{ {{ calculationData.size }} }={{ meanStandardDeviationFormatting[0] }}\]
        </p>
        <p v-else class="formulas math-jax-top-bottom-margins">
          \[\mu=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{ {{ additionString }} }{ {{ calculationData.size }} }={{ meanStandardDeviationFormatting[0] }}\]
        </p>
      </li>
      <li>
        <p>
          Find the <span class="bolded-texts">{{ currentMethodName }} standard</span> deviation of the dataset:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas math-jax-top-bottom-margins">
          \[s=\sqrt{\sum_{i=1}^{n}{\frac{(X_i-\bar{X})^2}{n-1}=}}\sum_{i=1}^{n}{\frac{ {{ sqrString }} }{ {{ calculationData.size }}-1}={{ meanStandardDeviationFormatting[1] }}}\]
        </p>
        <p v-else class="formulas math-jax-top-bottom-margins">
          \[\sigma=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\mu)^2}{n}}=\sum_{i=1}^{n}{\frac{ {{ sqrString }} }{ {{ calculationData.size }} }={{ meanStandardDeviationFormatting[1] }}}\]
        </p>
      </li>
      <li>
        <p>
          Standardize each data point by subtracting the mean and dividing by the sample standard deviation. For example, for the first variable we have the following:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas math-jax-top-bottom-margins">
          \[Z=\frac{X_i-\bar{X}}{s}=\frac{ {{ calculationData.rawDataMeanArray[0] }}-{{ meanStandardDeviationFormatting[0] }} }{ {{ meanStandardDeviationFormatting[1] }} }={{ standardizeExampleResult }}\]
        </p>
        <p v-else class="formulas math-jax-top-bottom-margins">
          \[Z=\frac{X_i-\mu}{\sigma}=\frac{ {{ calculationData.rawDataMeanArray[0] }}-{{ meanStandardDeviationFormatting[0] }} }{ {{ meanStandardDeviationFormatting[1] }} }={{ standardizeExampleResult }}\]
        </p>
        <p class="bottom-margins">
          We obtain the rest of the standardized variables in the same way.
        </p>
      </li>
      <li>
        <p>
          Cube each standardized value by raising it to the power of 3:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas math-jax-top-bottom-margins">
          \[\left(\frac{X_i-\bar{X}}{s}\right)^3=\left(\frac{ {{ calculationData.rawDataMeanArray[0] }}-{{ meanStandardDeviationFormatting[0] }} }{ {{ meanStandardDeviationFormatting[1] }} }\right)^3={{ powerOfThree }}\]
        </p>
        <p v-else class="formulas math-jax-top-bottom-margins">
          \[\left(\frac{X_i-\mu}{\sigma}\right)^3=\left(\frac{ {{ calculationData.rawDataMeanArray[0] }}-{{ meanStandardDeviationFormatting[0] }} }{ {{ meanStandardDeviationFormatting[1] }} }\right)^3={{ powerOfThree }}\]
        </p>
      </li>
      <li>
        <p>
          Sum all the cubed standardized values:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas math-jax-top-bottom-margins">
          \[\sum_{i=1}^{n}{\left(\frac{X_i-\bar{X}}{s}\right)^3=\sum_{i=1}^{n}{ {{ cubedString }} }={{ calculationData.cubedStandardizationsValuesSum }}}\]
        </p>
        <p v-else class="formulas math-jax-top-bottom-margins">
          \[\sum_{i=1}^{n}{\left(\frac{X_i-\mu}{\sigma}\right)^3=\sum_{i=1}^{n}{ {{ cubedString }} }={{ calculationData.cubedStandardizationsValuesSum }}}\]
        </p>
      </li>
      <li>
        <div v-if="calculationData.method === 'sample'">
          <p>
            Multiplying the result by n/(n-1) (n-2) to obtain the <span class="bolded-texts">sample skewness</span>
          </p>
          <p class="formulas top-margins">
            \[S_K=\left[\frac{ {{ calculationData.size }} }{({{ calculationData.size }}-1)({{ calculationData.size }}-2)}\right]\sum_{i=1}^{n}\left(\frac{X_i-\bar{X}}{s}\right)^3\]
          </p>
          <p class="formulas">
            \[S_K=\left[\frac{n}{(n-1)(n-2)}\right]\sum_{i=1}^{n}{\left(\frac{X_i-\bar{X}}{s}\right)^3=}\left[\frac{ {{ calculationData.size }} }{({{ calculationData.size }}-1)({{ calculationData.size }}-2)}\right]({{ calculationData.cubedStandardizationsValuesSum }})={{ calculationData.skewness }}\]
          </p>
        </div>
        <div v-else>
          <p>
            Multiplying the result by 1/N (the number of observations) to obtain the <span class="bolded-texts">population skewness</span>
          </p>
          <p class="formulas top-margins">
            \[\gamma_1=\left[\frac{1}{N}\right]\sum_{i=1}^{n}{\left(\frac{X_i-\bar{X}}{s}\right)^3=\left[\frac{1}{ {{ calculationData.size }} }\right]({{ calculationData.cubedStandardizationsValuesSum }})={{ calculationData.skewness }}}\]
          </p>
        </div>
      </li>
    </ol>

    <div v-if="calculationData.method === 'sample'">
      <div class="python-r-codes python">
        <span class="img-text"><img alt="Python icon" src="../../../../assets/images/icon-python-calc.png"> <h4 class="h6">Your Result in Python:</h4></span>
        <p class="codes-info">
          The following Python code should provide the skewness using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-python"
            ><code id="python-sample"># Import relevant library

from scipy.stats import skew

data = <span class="no-wrap">{{ inputToNumbers }}</span>

# Calculate the sample skewness using scipy.stats

skewness = skew(data, bias=False)

print("Sample skewness:", skewness)</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode('python-sample')">
          {{ copyCodeOrCopiedPython }}
        </button>
      </div>
      <div class="python-r-codes">
        <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
        <p class="codes-info">
          The following R code should provide the skewness using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-r"
            ><code id="r-sample"># Install and load the e1071 package (if not installed)

install.packages("e1071")

library(e1071)

# Create vector of data

data &lt;- c(<span class="no-wrap">{{ arrayToString }}</span>)

# Calculate sample skewness using e1071 package

skewness(data, type = 2)</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode('r-sample')">
          {{ copyCodeOrCopiedR }}
        </button>
      </div>
    </div>
    <div v-else>
      <div class="python-r-codes python">
        <span class="img-text"><img alt="Python icon" src="../../../../assets/images/icon-python-calc.png"> <h4 class="h6">Your Result in Python:</h4></span>
        <p class="codes-info">
          The following Python code should provide the skewness using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-python"
            ><code id="python-population"># Import relevant library

from scipy.stats import skew

data = <span class="no-wrap">{{ inputToNumbers }}</span>

# Calculate the population skewness using scipy.stats

skewness = skew(data)

print("Population skewness:", skewness)</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode('python-population')">
          {{ copyCodeOrCopiedPython }}
        </button>
      </div>
      <div class="python-r-codes">
        <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
        <p class="codes-info">
          The following R code should provide the skewness using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-r"
            ><code id="r-population"># Install and load the e1071 package (if not installed)

install.packages("e1071")

library(e1071)

# Create vector of data

data &lt;- c(<span class="no-wrap">{{ arrayToString }}</span>)

# Calculate population skewness using e1071 package

skewness(data, type = 1)</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode('r-population')">
          {{ copyCodeOrCopiedR }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  data() {
    return {
      copyCodeOrCopiedPython: 'Copy Code',
      copyCodeOrCopiedR: 'Copy Code',
    };
  },
  computed: {
    ...mapState('calculators', ['result', 'calculationData']),
    additionString() {
      const { length } = this.calculationData.rawDataMeanArray;
      const rawDataMeanArray = this.calculationData.rawDataMeanArray.map((num, i) => (length - 1 > i ? `${num} + ` : num));
      return rawDataMeanArray.join('');
    },
    sqrString() {
      const { length } = this.calculationData.rawDataMeanArray;
      const rawDataMeanArray = this.calculationData.rawDataMeanArray.map((num, i) => (length - 1 > i ? `(${num} - ${this.meanStandardDeviationFormatting[0]})^2 + ` : `(${num} - ${this.meanStandardDeviationFormatting[0]})^2`));
      return rawDataMeanArray.join('');
    },
    cubedString() {
      const { length } = this.calculationData.rawDataMeanArray;
      const rawDataMeanArray = this.calculationData.rawDataMeanArray.map((num, i) => (length - 1 > i ? `\\left(\\frac{${num}-${this.meanStandardDeviationFormatting[0]}}{${this.meanStandardDeviationFormatting[1]}}\\right)^3 + ` : `\\left(\\frac{${num}-${this.meanStandardDeviationFormatting[0]}}{${this.meanStandardDeviationFormatting[1]}}\\right)^3`));
      return rawDataMeanArray.join('');
    },
    standardizeExampleResult() {
      return ((this.calculationData.rawDataMeanArray[0] - this.calculationData.mean) / this.calculationData.standardDeviation).toFixed(this.calculationData.selectedDecimalPlaces);
    },
    powerOfThree() {
      return (((this.calculationData.rawDataMeanArray[0] - this.calculationData.mean) / this.calculationData.standardDeviation) ** 3).toFixed(this.calculationData.selectedDecimalPlaces);
    },
    meanStandardDeviationFormatting() {
      return [this.calculationData.mean.toFixed(this.calculationData.selectedDecimalPlaces), this.calculationData.standardDeviation.toFixed(this.calculationData.selectedDecimalPlaces)];
    },
    inputToNumbers() {
      return this.calculationData.rawDataMeanArray.map((str) => Number(str));
    },
    arrayToString() {
      return this.calculationData.rawDataMeanArray.join(', ');
    },
    currentMethodName() {
      return this.calculationData.method === 'sample' ? 'sample' : 'population';
    },
  },
  methods: {
    copyCode(id) {
      if (id.split('-')[0] === 'python') {
        this.copyCodeOrCopiedPython = 'Copied';
        setTimeout(() => {
          this.copyCodeOrCopiedPython = 'Copy Code';
        }, 1000);
      } else {
        this.copyCodeOrCopiedR = 'Copied';
        setTimeout(() => {
          this.copyCodeOrCopiedR = 'Copy Code';
        }, 1000);
      }

      const currentText = document.getElementById(id).innerText;
      this.$copy(currentText);
    },
  },
};
</script>

<style lang="scss" scoped>
.calculation-steps {
  p {
    margin: 0;
  }

  .python-r-codes.python {
    margin-top: var(--spacing-80);
  }

  ul {
    padding-left: 0;
  }

  .additional-h2 {
    margin-top: var(--spacing-40);
  }

  .second-additional-h2 {
    margin-top: var(--spacing-24);
  }

  .math-jax-bottom-margins {
    margin-bottom: var(--spacing-16);
  }

  .math-jax-top-bottom-margins {
    margin: var(--spacing-16) 0;
  }

  .colored-texts {
    font-weight: 600;
    color: var(--accent-shade-3);
  }
}

@include bp($bp-mobile) {
  div .calculation-steps {
    padding-top: var(--spacing-40);

    .h4 {
      text-align: left;
    }
  }
}

@import '../../../../assets/sass/ds/calculators/calculation-steps.scss';
</style>
